import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Wind, Play, Pause, X, Crown, ChevronRight, 
  Volume2, VolumeX, Check, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { hapticLight, hapticSuccess } from '../lib/nativeFeel';
import { useAudioInterruption } from '../lib/nativeAudio';
import RitualCard from './RitualCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phases of breathing cycle
const PHASES = {
  IDLE: 'idle',
  INTRO: 'intro',
  INHALE: 'inhale',
  HOLD: 'hold',
  EXHALE: 'exhale',
  COMPLETE: 'complete'
};

// Phase display labels
const PHASE_LABELS = {
  [PHASES.IDLE]: 'Ready',
  [PHASES.INTRO]: 'Prepare',
  [PHASES.INHALE]: 'Inhale',
  [PHASES.HOLD]: 'Hold',
  [PHASES.EXHALE]: 'Exhale',
  [PHASES.COMPLETE]: 'Complete'
};

// Phase colors for ring
const PHASE_COLORS = {
  [PHASES.IDLE]: '#8BAA9E',
  [PHASES.INTRO]: '#C6A75E',
  [PHASES.INHALE]: '#8BAA9E',
  [PHASES.HOLD]: '#C6A75E',
  [PHASES.EXHALE]: '#B8AFA6',
  [PHASES.COMPLETE]: '#8BAA9E'
};

const Breathwork = ({ onComplete, onClose, done, onSessionComplete }) => {
  const { user } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState(PHASES.IDLE);
  const [phaseCount, setPhaseCount] = useState(0);
  const [currentCycle, setCurrentCycle] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  
  // Config state
  const [config, setConfig] = useState({
    inhale: 4,
    hold: 7,
    exhale: 8,
    cycles: 7
  });
  
  // Audio state - CUE-BASED approach
  const [isMuted, setIsMuted] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [audioCues, setAudioCues] = useState(null); // { begin_url, inhale_url, hold_url, exhale_url, complete_url, chime_url }
  const [audioLoading, setAudioLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [mode, setMode] = useState('guided'); // 'guided' | 'silent'
  
  // Audio refs for cue-based playback
  const beginAudioRef = useRef(null);
  const inhaleAudioRef = useRef(null);
  const holdAudioRef = useRef(null);
  const exhaleAudioRef = useRef(null);
  const completeAudioRef = useRef(null);
  const chimeRef = useRef(null);
  const timerRef = useRef(null);

  // Pause cue audio if the app is backgrounded during an active session
  // (breathwork cues are short so we only resume the timer/phase state via
  // the existing tick; we mainly want to stop cues playing behind the
  // lock screen unexpectedly). Zero-op on the web build.
  useAudioInterruption({
    getRefs: () => [beginAudioRef, inhaleAudioRef, holdAudioRef, exhaleAudioRef, completeAudioRef, chimeRef],
    isSessionActive: () => isActive && !isPaused,
  });

  // Fetch available voices on mount
  useEffect(() => {
    const fetchVoices = async () => {
      if (!user?.is_premium) return;
      
      try {
        const response = await fetch(`${API_URL}/api/breathwork/voices`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data?.voices) {
            setAvailableVoices(data.data.voices);
            // Set default voice
            if (data.data.default_voice_id) {
              const defaultVoice = data.data.voices.find(v => v.voice_id === data.data.default_voice_id || v.id === data.data.default_voice_id);
              if (defaultVoice) setSelectedVoice(defaultVoice);
            } else if (data.data.voices.length > 0) {
              setSelectedVoice(data.data.voices[0]);
            }
          }
        }
      } catch (error) {
      }
    };
    fetchVoices();
  }, [user?.is_premium]);

  // Calculate phase duration
  const getPhaseDuration = useCallback((currentPhase) => {
    switch (currentPhase) {
      case PHASES.INHALE: return config.inhale;
      case PHASES.HOLD: return config.hold;
      case PHASES.EXHALE: return config.exhale;
      case PHASES.INTRO: return 3; // 3 second intro
      default: return 0;
    }
  }, [config]);

  // Get next phase
  const getNextPhase = useCallback((currentPhase, cycle) => {
    switch (currentPhase) {
      case PHASES.INTRO: return PHASES.INHALE;
      case PHASES.INHALE: return PHASES.HOLD;
      case PHASES.HOLD: return PHASES.EXHALE;
      case PHASES.EXHALE:
        if (cycle >= config.cycles) return PHASES.COMPLETE;
        return PHASES.INHALE;
      default: return PHASES.IDLE;
    }
  }, [config.cycles]);

  // Play audio cue for a specific phase
  const playCue = useCallback((cueType) => {
    if (isMuted || mode === 'silent' || !audioCues) return;
    
    let audioRef;
    let volume = 1.0; // Default volume
    
    switch (cueType) {
      case 'begin': audioRef = beginAudioRef; break;
      case 'inhale': audioRef = inhaleAudioRef; break;
      case 'hold': audioRef = holdAudioRef; break;
      case 'exhale': audioRef = exhaleAudioRef; break;
      case 'complete': audioRef = completeAudioRef; break;
      case 'chime': 
        audioRef = chimeRef; 
        volume = 0.25; // Softer chime for calm ending
        break;
      default: return;
    }
    
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.volume = volume;
      audioRef.current.play().catch(err => {
      });
    }
  }, [isMuted, mode, audioCues]);

  // Start breathwork session
  const startSession = async () => {
    setAudioLoading(true);
    
    try {
      const voiceId = selectedVoice?.voice_id || selectedVoice?.id || null;
      const isGuided = voiceId && voiceId !== 'none';
      
      const response = await fetch(`${API_URL}/api/breathwork/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          method_type: '4-7-8',
          voice_id: voiceId,
          audio_guided: isGuided,
          chime_enabled: true
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || error.message || 'Failed to start session');
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to start breathwork session');
      }
      
      const data = result.data;
      
      // Set session data from backend response
      setSessionId(data.session_id);
      setMode(data.mode);
      setConfig({
        inhale: data.timing.inhale,
        hold: data.timing.hold,
        exhale: data.timing.exhale,
        cycles: data.timing.cycles
      });
      
      // Set audio cues if guided mode
      if (data.mode === 'guided' && data.audio) {
        setAudioCues(data.audio);
      }
      
      setIsActive(true);
      setPhase(PHASES.INTRO);
      setPhaseCount(3);
      setCurrentCycle(0);
      setIsComplete(false);
      
      toast.success('Breathwork session started');
      
      // Play begin cue after a short delay
      setTimeout(() => {
        if (data.mode === 'guided' && data.audio?.begin_url) {
          playCue('begin');
        }
      }, 300);
      
    } catch (error) {
      toast.error(error.message || 'Failed to start breathwork session');
    } finally {
      setAudioLoading(false);
    }
  };

  // Handle phase transition - CRITICAL: Play cue at exact phase boundary
  const handlePhaseTransition = useCallback((nextPhase) => {
    // Play the appropriate cue for the new phase
    switch (nextPhase) {
      case PHASES.INHALE:
        playCue('inhale');
        hapticLight();
        break;
      case PHASES.HOLD:
        playCue('hold');
        break;
      case PHASES.EXHALE:
        playCue('exhale');
        hapticLight();
        break;
      case PHASES.COMPLETE:
        playCue('complete');
        hapticSuccess();
        // Small delay then chime
        setTimeout(() => playCue('chime'), 2000);
        break;
      default:
        break;
    }
  }, [playCue]);

  // Handle session completion
  const handleComplete = useCallback(async () => {
    // Mark session complete on backend
    if (sessionId) {
      try {
        await fetch(`${API_URL}/api/breathwork/${sessionId}/complete`, {
          method: 'POST',
          credentials: 'include'
        });
        if (onSessionComplete) onSessionComplete();
      } catch (error) {
      }
    }
  }, [sessionId, onSessionComplete]);

  // Use ref to track current cycle to avoid dependency issues
  const currentCycleRef = useRef(currentCycle);
  useEffect(() => {
    currentCycleRef.current = currentCycle;
  }, [currentCycle]);

  // Run the breathing timer
  useEffect(() => {
    if (!isActive || isPaused || phase === PHASES.IDLE || phase === PHASES.COMPLETE) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setPhaseCount(prev => {
        if (prev <= 1) {
          // When exhale ends, increment cycle FIRST then check if complete
          if (phase === PHASES.EXHALE) {
            const newCycle = currentCycleRef.current + 1;
            setCurrentCycle(newCycle);
            
            // Check if this was the last cycle
            if (newCycle >= config.cycles) {
              // Session complete
              setPhase(PHASES.COMPLETE);
              setIsComplete(true);
              handlePhaseTransition(PHASES.COMPLETE);
              handleComplete();
              return 0;
            } else {
              // Continue to next cycle - go to inhale
              handlePhaseTransition(PHASES.INHALE);
              setPhase(PHASES.INHALE);
              return getPhaseDuration(PHASES.INHALE);
            }
          }
          
          // For non-exhale phases, just move to next phase
          const nextPhase = getNextPhase(phase, currentCycleRef.current);
          
          if (nextPhase === PHASES.COMPLETE) {
            setPhase(PHASES.COMPLETE);
            setIsComplete(true);
            handlePhaseTransition(nextPhase);
            handleComplete();
            return 0;
          }
          
          // Trigger phase transition with cue playback
          handlePhaseTransition(nextPhase);
          setPhase(nextPhase);
          return getPhaseDuration(nextPhase);
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive, isPaused, phase, config.cycles, getNextPhase, getPhaseDuration, handlePhaseTransition, handleComplete]);

  // Toggle pause
  const togglePause = () => {
    setIsPaused(prev => !prev);
  };

  // End session early
  const endSession = () => {
    setIsActive(false);
    setPhase(PHASES.IDLE);
    setPhaseCount(0);
    setCurrentCycle(0);
    setIsPaused(false);
    setAudioCues(null);
    if (onClose) onClose();
  };

  // Continue to affirmation - close breathwork and open affirmations
  const continueToAffirmation = () => {
    // Reset all breathwork state
    setIsActive(false);
    setIsComplete(false);
    setPhase(PHASES.IDLE);
    setCurrentCycle(0);
    setPhaseCount(0);
    setShowSettings(false);
    setAudioCues(null);
    
    // Call the completion callback to open affirmations
    if (onComplete) {
      onComplete();
    }
  };

  // Calculate ring progress
  const getProgress = () => {
    if (phase === PHASES.IDLE || phase === PHASES.COMPLETE) return 0;
    const duration = getPhaseDuration(phase);
    if (duration === 0) return 0;
    return ((duration - phaseCount) / duration) * 100;
  };

  // Calculate overall session progress
  const getOverallProgress = () => {
    if (!isActive) return 0;
    const totalCycles = config.cycles;
    const cycleProgress = (currentCycle / totalCycles) * 100;
    return Math.min(cycleProgress, 100);
  };

  const ringProgress = getProgress();
  const ringColor = PHASE_COLORS[phase] || '#8BAA9E';

  // Non-premium locked state
  if (!user?.is_premium) {
    return (
      <RitualCard
        index={1}
        title="Guided Breathwork"
        image="/images/home/breathwork.jpg"
        actionLabel="Start"
        locked
        testId="breathwork-locked-btn"
        onClick={() => toast.info('Upgrade to Premium for guided breathwork sessions')}
      />
    );
  }

  // Active session - Full screen overlay
  if (isActive) {
    return (
      <div 
        className="fixed inset-0 z-[100] bg-[#1C1C1E] flex flex-col items-center justify-center"
        data-testid="breathwork-session-overlay"
      >
        {/* Hidden audio elements for cue-based playback */}
        {audioCues?.begin_url && (
          <audio ref={beginAudioRef} src={audioCues.begin_url} preload="auto" />
        )}
        {audioCues?.inhale_url && (
          <audio ref={inhaleAudioRef} src={audioCues.inhale_url} preload="auto" />
        )}
        {audioCues?.hold_url && (
          <audio ref={holdAudioRef} src={audioCues.hold_url} preload="auto" />
        )}
        {audioCues?.exhale_url && (
          <audio ref={exhaleAudioRef} src={audioCues.exhale_url} preload="auto" />
        )}
        {audioCues?.complete_url && (
          <audio ref={completeAudioRef} src={audioCues.complete_url} preload="auto" />
        )}
        {audioCues?.chime_url && (
          <audio ref={chimeRef} src={audioCues.chime_url} preload="auto" />
        )}

        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1C1E] via-[#252528] to-[#1C1C1E]" />
        
        {/* Ambient glow effect */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full transition-all duration-1000"
          style={{ 
            background: `radial-gradient(circle, ${ringColor}20 0%, transparent 70%)`,
            transform: `translate(-50%, -50%) scale(${phase === PHASES.INHALE ? 1.2 : phase === PHASES.EXHALE ? 0.8 : 1})`
          }}
        />

        {/* Header controls */}
        <header className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
          <button
            onClick={endSession}
            className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-xl flex items-center justify-center text-[#DCD3CA] hover:bg-black/50 transition-all"
            data-testid="end-breathwork-btn"
            aria-label="End breathwork session"
          >
            <X className="w-5 h-5" />
          </button>
          
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-xl flex items-center justify-center text-[#DCD3CA] hover:bg-black/50 transition-all"
            data-testid="mute-breathwork-btn"
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </header>

        {/* Main content */}
        <div className="relative z-10 flex flex-col items-center">
          {isComplete ? (
            /* Completion screen */
            <div className="text-center animate-fade-in" data-testid="breathwork-complete">
              <div className="w-24 h-24 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center mx-auto mb-6">
                <Check className="w-12 h-12 text-[#8BAA9E]" />
              </div>
              <p className="font-serif text-3xl text-[#DCD3CA] mb-2">You are regulated.</p>
              <p className="text-[#B5B1AB] font-sans text-sm mb-8">
                Your nervous system is calm and ready.
              </p>
              <Button
                onClick={continueToAffirmation}
                className="bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-full px-8 py-6 text-base font-medium"
                data-testid="continue-to-affirmation-btn"
              >
                Continue to Affirmation
              </Button>
            </div>
          ) : (
            /* Active breathing UI */
            <>
              {/* Cycle indicator */}
              <p className="text-[#C6A75E] text-xs tracking-[0.3em] uppercase mb-8 font-sans">
                Cycle {Math.min(currentCycle + 1, config.cycles)}/{config.cycles}
              </p>

              {/* Circular ring timer */}
              <div className="relative w-64 h-64 mb-8">
                {/* Background ring */}
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 256 256">
                  <circle 
                    cx="128" cy="128" r="110" 
                    fill="none" 
                    stroke="rgba(209, 200, 191, 0.1)" 
                    strokeWidth="6" 
                  />
                  {/* Progress ring */}
                  <circle
                    cx="128" cy="128" r="110"
                    fill="none"
                    stroke={ringColor}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 110}`}
                    strokeDashoffset={`${2 * Math.PI * 110 * (1 - ringProgress / 100)}`}
                    className="transition-all duration-300 ease-linear"
                    style={{ 
                      filter: `drop-shadow(0 0 8px ${ringColor}50)`
                    }}
                  />
                </svg>

                {/* Center content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p 
                    className="font-serif text-4xl text-[#DCD3CA] mb-2 transition-all duration-500"
                    style={{ 
                      transform: phase === PHASES.INHALE ? 'scale(1.1)' : phase === PHASES.EXHALE ? 'scale(0.95)' : 'scale(1)',
                      opacity: isPaused ? 0.5 : 1
                    }}
                  >
                    {PHASE_LABELS[phase]}
                  </p>
                  <p className="font-serif text-6xl text-[#DCD3CA] tabular-nums">
                    {phaseCount}
                  </p>
                </div>

                {/* Breathing scale indicator */}
                <div 
                  className="absolute inset-0 rounded-full border-2 transition-all duration-1000 ease-in-out"
                  style={{ 
                    borderColor: `${ringColor}30`,
                    transform: phase === PHASES.INHALE 
                      ? 'scale(1.08)' 
                      : phase === PHASES.EXHALE 
                        ? 'scale(0.92)' 
                        : 'scale(1)'
                  }}
                />
              </div>

              {/* Phase instruction */}
              <p className="text-[#B5B1AB] font-sans text-sm mb-10 h-6">
                {phase === PHASES.INTRO && 'Relax your shoulders...'}
                {phase === PHASES.INHALE && 'Breathe in through your nose...'}
                {phase === PHASES.HOLD && 'Hold gently...'}
                {phase === PHASES.EXHALE && 'Release slowly through your mouth...'}
              </p>

              {/* Controls */}
              <div className="flex items-center gap-4">
                <Button
                  onClick={togglePause}
                  className={`w-16 h-16 rounded-full ${
                    isPaused 
                      ? 'bg-[#8BAA9E] hover:bg-[#A3C2B6]' 
                      : 'bg-[#252528] hover:bg-[#333336]'
                  } flex items-center justify-center transition-all`}
                  data-testid="pause-breathwork-btn"
                >
                  {isPaused ? (
                    <Play className="w-7 h-7 text-[#1C1C1E] ml-1" fill="currentColor" />
                  ) : (
                    <Pause className="w-7 h-7 text-[#DCD3CA]" fill="currentColor" />
                  )}
                </Button>
              </div>

              {isPaused && (
                <p className="text-[#C6A75E] text-xs font-sans mt-4 animate-pulse">
                  Paused - Tap to resume
                </p>
              )}
            </>
          )}
        </div>

        {/* Overall progress bar at bottom */}
        {!isComplete && (
          <div className="absolute bottom-8 left-8 right-8">
            <div className="h-1 bg-[#252528] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#8BAA9E]/50 transition-all duration-500"
                style={{ width: `${getOverallProgress()}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Idle state - Dashboard button with settings
  return (
    <div data-testid="breathwork-container">
      {/* Main Breathwork Button */}
      <RitualCard
        index={1}
        title="Guided Breathwork"
        subtitle={`4-7-8 technique • ${config.cycles} cycles`}
        image="/images/home/breathwork.jpg"
        actionLabel="Start"
        done={done}
        testId="breathwork-btn"
        onClick={() => setShowSettings(true)}
      />

      {/* Settings Modal */}
      {showSettings && (
        <div 
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4 overflow-y-auto"
          onClick={() => setShowSettings(false)}
        >
          <div 
            className="bg-[#252528] border border-white/10 rounded-[24px] p-5 sm:p-6 w-full max-w-md max-h-[92vh] overflow-y-auto my-2 sm:my-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center">
                <Wind className="w-6 h-6 text-[#8BAA9E]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-[#DCD3CA]">4-7-8 Breathwork</h3>
                <p className="text-[#7A7670] text-xs font-sans">Dr. Andrew Weil's technique</p>
              </div>
            </div>

            {/* Timing Display */}
            <div className="bg-[#1C1C1E] rounded-xl p-4 mb-6">
              <p className="text-[#8BAA9E] text-xs uppercase tracking-wider mb-3 font-sans">Breathing Pattern</p>
              <div className="flex justify-around">
                <div className="text-center">
                  <p className="font-serif text-2xl text-[#DCD3CA]">{config.inhale}</p>
                  <p className="text-[#7A7670] text-xs font-sans">Inhale</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-2xl text-[#C6A75E]">{config.hold}</p>
                  <p className="text-[#7A7670] text-xs font-sans">Hold</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-2xl text-[#C5BCB2]">{config.exhale}</p>
                  <p className="text-[#7A7670] text-xs font-sans">Exhale</p>
                </div>
              </div>
              <p className="text-[#7A7670] text-xs font-sans text-center mt-3">
                {config.cycles} cycles • ~{Math.round((config.inhale + config.hold + config.exhale) * config.cycles / 60)} minutes
              </p>
            </div>

            {/* Voice Selection - RESTRICTED TO 4 VOICES */}
            <div className="mb-6">
              <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-3 font-sans flex items-center gap-2">
                <Volume2 className="w-4 h-4" /> Voice Guide
              </p>
              
              <div className="space-y-2 max-h-[160px] sm:max-h-[200px] overflow-y-auto">
                {availableVoices.length > 0 ? (
                  availableVoices.map(voice => (
                    <button
                      key={voice.voice_id || voice.id}
                      onClick={() => setSelectedVoice(voice)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                        (selectedVoice?.voice_id || selectedVoice?.id) === (voice.voice_id || voice.id)
                          ? 'bg-[#8BAA9E]/20 border border-[#8BAA9E]/30'
                          : 'bg-[#1C1C1E] hover:bg-[#1C1C1E]/80'
                      }`}
                      data-testid={`voice-${voice.voice_id || voice.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-4 h-4 text-[#8BAA9E]" />
                        <div>
                          <span className="text-[#DCD3CA] font-sans text-sm">
                            {voice.label || voice.name}
                          </span>
                          <span className="text-[#7A7670] text-xs ml-2">
                            ({voice.description})
                          </span>
                        </div>
                      </div>
                      {(selectedVoice?.voice_id || selectedVoice?.id) === (voice.voice_id || voice.id) && (
                        <Check className="w-4 h-4 text-[#8BAA9E]" />
                      )}
                    </button>
                  ))
                ) : (
                  <p className="text-[#7A7670] text-xs font-sans text-center py-4">
                    Loading voices...
                  </p>
                )}
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-[#1C1C1E] rounded-xl p-4 mb-6">
              <p className="text-[#7A7670] text-xs font-sans leading-relaxed italic">
                "The 4-7-8 technique activates your parasympathetic nervous system, 
                reducing anxiety and preparing your mind for deep visualisation."
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowSettings(false)}
                variant="outline"
                className="flex-1 border-[#666666] text-[#B5B1AB] rounded-xl py-4"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowSettings(false);
                  startSession();
                }}
                disabled={audioLoading || !selectedVoice}
                className="flex-1 bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-xl py-4 font-medium"
                data-testid="begin-breathwork-btn"
              >
                {audioLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Preparing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" fill="currentColor" />
                    Begin Breathwork
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Breathwork;
